import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format, startOfDay, subDays } from 'date-fns';

export interface NutritionLog {
  id: string;
  user_id: string;
  logged_date: string;
  calories: number;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  meal_type: string | null;
  notes: string | null;
  created_at: string;
}

export interface DailyNutritionSummary {
  date: string;
  totalCalories: number;
  totalProtein: number;
  totalCarbs: number;
  totalFat: number;
  logs: NutritionLog[];
}

export function useNutritionLogs(userId: string | undefined) {
  const [logs, setLogs] = useState<NutritionLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [todaySummary, setTodaySummary] = useState<DailyNutritionSummary | null>(null);
  const [weekSummary, setWeekSummary] = useState<DailyNutritionSummary[]>([]);

  const fetchLogs = useCallback(async (days: number = 7) => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      const startDate = format(subDays(new Date(), days - 1), 'yyyy-MM-dd');
      
      const { data, error } = await supabase
        .from('nutrition_logs')
        .select('*')
        .eq('user_id', userId)
        .gte('logged_date', startDate)
        .order('logged_date', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;

      const fetchedLogs = (data || []) as NutritionLog[];
      setLogs(fetchedLogs);

      // Calculate today's summary
      const today = format(new Date(), 'yyyy-MM-dd');
      const todayLogs = fetchedLogs.filter(log => log.logged_date === today);
      setTodaySummary({
        date: today,
        totalCalories: todayLogs.reduce((sum, log) => sum + (log.calories || 0), 0),
        totalProtein: todayLogs.reduce((sum, log) => sum + (log.protein_g || 0), 0),
        totalCarbs: todayLogs.reduce((sum, log) => sum + (log.carbs_g || 0), 0),
        totalFat: todayLogs.reduce((sum, log) => sum + (log.fat_g || 0), 0),
        logs: todayLogs,
      });

      // Calculate week summary by day
      const summaryByDay: Record<string, DailyNutritionSummary> = {};
      for (let i = 0; i < days; i++) {
        const date = format(subDays(new Date(), i), 'yyyy-MM-dd');
        summaryByDay[date] = {
          date,
          totalCalories: 0,
          totalProtein: 0,
          totalCarbs: 0,
          totalFat: 0,
          logs: [],
        };
      }

      fetchedLogs.forEach(log => {
        if (summaryByDay[log.logged_date]) {
          summaryByDay[log.logged_date].totalCalories += log.calories || 0;
          summaryByDay[log.logged_date].totalProtein += log.protein_g || 0;
          summaryByDay[log.logged_date].totalCarbs += log.carbs_g || 0;
          summaryByDay[log.logged_date].totalFat += log.fat_g || 0;
          summaryByDay[log.logged_date].logs.push(log);
        }
      });

      setWeekSummary(Object.values(summaryByDay).sort((a, b) => 
        new Date(a.date).getTime() - new Date(b.date).getTime()
      ));

    } catch (error: any) {
      console.error('Error fetching nutrition logs:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const logNutrition = useCallback(async (entry: {
    calories: number;
    protein?: number;
    carbs?: number;
    fat?: number;
    mealType?: string;
    notes?: string;
    date?: string;
  }) => {
    if (!userId) return false;

    try {
      const logDate = entry.date || format(new Date(), 'yyyy-MM-dd');
      
      const { error } = await supabase
        .from('nutrition_logs')
        .insert({
          user_id: userId,
          logged_date: logDate,
          calories: entry.calories,
          protein_g: entry.protein || null,
          carbs_g: entry.carbs || null,
          fat_g: entry.fat || null,
          meal_type: entry.mealType || null,
          notes: entry.notes || null,
        });

      if (error) throw error;

      // Refresh logs
      await fetchLogs();
      return true;
    } catch (error: any) {
      console.error('Error logging nutrition:', error);
      toast.error('Failed to log nutrition');
      return false;
    }
  }, [userId, fetchLogs]);

  const deleteLog = useCallback(async (logId: string) => {
    if (!userId) return false;

    try {
      const { error } = await supabase
        .from('nutrition_logs')
        .delete()
        .eq('id', logId)
        .eq('user_id', userId);

      if (error) throw error;

      await fetchLogs();
      toast.success('Entry deleted');
      return true;
    } catch (error: any) {
      console.error('Error deleting nutrition log:', error);
      toast.error('Failed to delete entry');
      return false;
    }
  }, [userId, fetchLogs]);

  return {
    logs,
    loading,
    todaySummary,
    weekSummary,
    logNutrition,
    deleteLog,
    refetch: fetchLogs,
  };
}
